import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { IMAGES } from "@/lib/data";
import { useCart } from "@/context/CartContext";
import ResetLink from "@/components/ResetLink";
import useContent from "@/hooks/useContent";

const DEFAULT_LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/blogs", label: "Blogs" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { value } = useContent("navbar");

  // const links = value("links", DEFAULT_LINKS);
  const links = value("links", DEFAULT_LINKS);
  const exploreLabel = value("explore_button_label", "Explore Products");

  const dark = false; // Home hero is now a light poster slideshow; navbar always uses dark text on light bg

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const textCol = dark ? "text-[#f8f6f2]" : "text-[#2b2823]";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled ? "bg-[#f5f1ea]/85 backdrop-blur-xl border-b border-[#5c3e2b]/12" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <ResetLink to="/" data-testid="nav-logo" aria-label="Vanalume, Home" className="flex items-center shrink-0">
          <img
            src={IMAGES.logo}
            alt="Vanalume"
            className={`h-8 md:h-10 w-auto object-contain transition-[filter] duration-500 ${dark ? "invert" : ""}`}
          />
        </ResetLink>

        <div className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <ResetLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`vl-link-underline fs-nav_link tracking-wide transition-colors duration-300 ${textCol} ${pathname === l.to ? "opacity-100" : "opacity-70 hover:opacity-100"}`}
            >
              {l.label}
            </ResetLink>
          ))}
          <ResetLink
            to="/shop"
            data-testid="nav-explore-btn"
            className={`inline-block border fs-nav_button tracking-wide px-6 py-2.5 rounded-full transition-colors duration-300 ${
              dark ? "border-[#f8f6f2]/60 text-[#f8f6f2] hover:bg-[#f8f6f2] hover:text-[#2b2823]" : "border-[#5c3e2b] text-[#5c3e2b] hover:bg-[#5c3e2b] hover:text-[#f8f6f2]"
            }`}
          >
            {exploreLabel}
          </ResetLink>
          <button
            onClick={() => setCartOpen(true)}
            data-testid="nav-cart-btn"
            aria-label={`Open cart, ${count} items`}
            className={`relative p-2 -mr-2 transition-colors duration-300 ${textCol} hover:opacity-100 opacity-80`}
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#e6b980] text-[#2b2320] text-[10px] font-medium flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <button
            onClick={() => setCartOpen(true)}
            data-testid="nav-mobile-cart-btn"
            aria-label={`Open cart, ${count} items`}
            className={`relative p-2 ${textCol}`}
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute top-0 right-0 h-4 min-w-4 px-1 rounded-full bg-[#e6b980] text-[#2b2320] text-[10px] font-medium flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className={`p-2 ${textCol}`} aria-label="Toggle menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#f5f1ea]/97 backdrop-blur-xl border-b border-[#5c3e2b]/12"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <ResetLink key={l.to} to={l.to} data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="font-display text-3xl text-[#2b2823]">
                  {l.label}
                </ResetLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
